// AdminUsers — Block 4 (P1). Lists users with filters, KPI cards, daily-signup
// sparkline, conversion funnel and Super-Admin actions (block/unblock + plan change).
import React, { useEffect, useMemo, useState } from 'react';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import {
  Loader2, Users, UserCheck, Crown, AlertTriangle, Search, Lock, Unlock, RefreshCw,
  UserPlus, Pencil, KeyRound, Copy, Check, X, MessageCircle, AlertCircle, Mail,
} from 'lucide-react';

const SUPPORT_WHATSAPP = '5581981739909';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const Kpi = ({ icon: Icon, color, label, value, note, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="w-9 h-9 rounded-md flex items-center justify-center"
           style={{ background: color + '15', color }}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-[11px] uppercase tracking-wider text-[#475569]">{label}</p>
    <p className="text-2xl font-black text-[#0F1C2E]">{value}</p>
    {note && <p className="text-[10px] text-[#94A3B8] mt-1">{note}</p>}
  </div>
);

// Tiny inline bar chart (no external dep) for daily signups.
const Sparkline = ({ series, color = '#1E3A5F' }) => {
  if (!series || series.length === 0) {
    return <p className="text-xs text-[#94A3B8]">Sem cadastros nos últimos 30 dias.</p>;
  }
  const max = Math.max(1, ...series.map((p) => p.count));
  return (
    <div className="flex items-end gap-1 h-24">
      {series.map((p, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0">
          <div
            className="w-full rounded-t"
            style={{ height: `${(p.count / max) * 100}%`, background: color, minHeight: p.count ? 2 : 0 }}
            title={`${p.date}: ${p.count}`}
          />
        </div>
      ))}
    </div>
  );
};

const Funnel = ({ data }) => {
  if (!data) return null;
  const stages = [
    { label: 'Cadastraram', value: data.cadastraram, color: '#1E3A5F' },
    { label: 'Usaram (>= 1 consulta)', value: data.usaram, color: '#006030' },
    { label: 'Converteram (Pro)', value: data.converteram, color: '#F59E0B' },
  ];
  const max = Math.max(1, ...stages.map((s) => s.value));
  return (
    <div className="space-y-2" data-testid="users-funnel">
      {stages.map((s) => (
        <div key={s.label}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#475569]">{s.label}</span>
            <span className="font-bold text-[#0F1C2E]">{s.value}</span>
          </div>
          <div className="w-full h-2 bg-[#F4F6F8] rounded">
            <div className="h-2 rounded" style={{ width: `${(s.value / max) * 100}%`, background: s.color }} />
          </div>
        </div>
      ))}
      <div className="flex justify-between text-[10px] text-[#94A3B8] pt-1 border-t border-[#F4F6F8]">
        <span>Taxa de uso: <strong>{data.taxa_uso}%</strong></span>
        <span>Taxa de conversão: <strong>{data.taxa_conversao}%</strong></span>
      </div>
    </div>
  );
};

const PlanBadge = ({ plano }) => {
  const map = {
    pro: ['bg-[#FEF3C7] text-[#92400E]', 'Pro'],
    free: ['bg-[#F4F6F8] text-[#475569]', 'Free'],
  };
  const [cls, label] = map[plano] || map.free;
  return <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${cls}`}>{label}</span>;
};

const StatusBadge = ({ status }) => {
  if (status === 'bloqueado') {
    return <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-[#FEE2E2] text-[#7F1D1D]">Bloqueado</span>;
  }
  if (status === 'inativo') {
    return <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-[#F4F6F8] text-[#94A3B8]">Inativo</span>;
  }
  return <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-[#DCFCE7] text-[#006030]">Ativo</span>;
};

const UFS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

// ──────────────────────────────────────────────────────────────────────────
// Modais (criar / editar / reset de senha)
// ──────────────────────────────────────────────────────────────────────────

const ModalShell = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-black text-[#0F1C2E]">{title}</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569]"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const FieldErr = ({ msg }) => msg ? (
  <p className="text-[10px] text-[#C62828] mt-0.5">{msg}</p>
) : null;

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-wider font-bold text-[#475569]">{children}</label>
);

const CreateUserModal = ({ open, onClose, onCreated }) => {
  const { token } = useAdminAuth();
  const [form, setForm] = useState({
    nome: '', email: '', telefone: '', plano: 'free', role: 'user',
    status: 'ativo', observacoes: '', enviar_email: false, must_change_password: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null); // { senha_temporaria, user, email_enviado }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ nome: '', email: '', telefone: '', plano: 'free', role: 'user',
                status: 'ativo', observacoes: '', enviar_email: false, must_change_password: true });
      setSubmitting(false); setErr(''); setResult(null); setCopied(false);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(''); setSubmitting(true);
    try {
      const resp = await adminAxios(token).post('/api/admin/users', form);
      setResult(resp.data);
      onCreated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Falha ao criar usuário.');
    } finally { setSubmitting(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result?.senha_temporaria || ''); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch { /* */ }
  };

  const whatsLink = result ? `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
    `Olá ${result.user.nome}, sua conta no Tô de Olho foi criada.\nE-mail: ${result.user.email}\nSenha temporária: ${result.senha_temporaria}\n\nAcesse: https://app.todeolho.inf.br/auth`
  )}` : '#';

  return (
    <ModalShell open={open} onClose={onClose} title="Novo usuário" testid="users-create-modal">
      {!result ? (
        <form onSubmit={submit} className="space-y-3">
          {err && (
            <div className="flex items-start gap-2 p-2 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{err}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome completo *</Label>
              <input value={form.nome} onChange={(e)=>setForm({...form, nome:e.target.value})}
                     required minLength={2} maxLength={120}
                     className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-create-nome" />
            </div>
            <div>
              <Label>E-mail *</Label>
              <input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
                     required className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-create-email" />
            </div>
            <div>
              <Label>Telefone</Label>
              <input value={form.telefone} onChange={(e)=>setForm({...form, telefone:e.target.value})}
                     placeholder="+55 81 9..." className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-create-telefone" />
            </div>
            <div>
              <Label>Plano</Label>
              <select value={form.plano} onChange={(e)=>setForm({...form, plano:e.target.value})}
                      className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-create-plano">
                <option value="free">Free</option>
                <option value="pro">Pro (30 dias)</option>
              </select>
            </div>
            <div>
              <Label>Status inicial</Label>
              <select value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})}
                      className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-create-status">
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label>Observações internas</Label>
              <textarea value={form.observacoes} onChange={(e)=>setForm({...form, observacoes:e.target.value})}
                        rows={2} maxLength={1000}
                        className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-create-obs" />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-xs text-[#475569]">
              <input type="checkbox" checked={form.must_change_password}
                     onChange={(e)=>setForm({...form, must_change_password:e.target.checked})}
                     data-testid="users-create-must-change" />
              Exigir troca de senha no próximo login
            </label>
            <label className="col-span-2 flex items-center gap-2 text-xs text-[#475569]">
              <input type="checkbox" checked={form.enviar_email}
                     onChange={(e)=>setForm({...form, enviar_email:e.target.checked})}
                     data-testid="users-create-enviar-email" />
              <Mail className="w-3.5 h-3.5" /> Enviar e-mail de boas-vindas automaticamente (Resend)
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#475569] hover:bg-[#F8FAFC]"
                    data-testid="users-create-cancel">Cancelar</button>
            <button type="submit" disabled={submitting}
                    className="flex-1 bg-[#006030] hover:bg-[#004020] text-white font-bold px-3 py-2 rounded text-sm disabled:opacity-60"
                    data-testid="users-create-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Criar usuário'}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="rounded border border-[#FCD34D] bg-[#FFFBEB] p-3">
            <p className="text-[11px] uppercase font-bold text-[#92400E] mb-1">Senha temporária — copie agora</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-base font-black text-[#0F1C2E] bg-white border border-[#FDE68A] rounded px-3 py-2 select-all"
                    data-testid="users-create-temp-pw">{result.senha_temporaria}</code>
              <button onClick={copy}
                      className="px-2.5 py-2 bg-[#1E3A5F] hover:bg-[#16304b] text-white rounded text-xs font-bold inline-flex items-center gap-1"
                      data-testid="users-create-copy">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
              </button>
            </div>
            <p className="text-[10px] text-[#92400E] mt-2">
              Esta senha não será exibida novamente. {result.email_enviado
                ? 'O usuário também receberá por e-mail.'
                : 'O usuário será obrigado a trocá-la no primeiro login.'}
            </p>
            {result.email_error && (
              <p className="text-[10px] text-[#C62828] mt-1">E-mail falhou: {result.email_error}</p>
            )}
          </div>
          <div className="text-xs text-[#475569]">
            <div><strong>Para:</strong> {result.user.nome}</div>
            <div><strong>E-mail:</strong> {result.user.email}</div>
          </div>
          <a href={whatsLink} target="_blank" rel="noopener noreferrer"
             className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-3 py-2 rounded text-sm"
             data-testid="users-create-whatsapp">
            <MessageCircle className="w-4 h-4" /> Enviar credenciais pelo WhatsApp
          </a>
          <button onClick={onClose}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#475569] hover:bg-[#F8FAFC]"
                  data-testid="users-create-close">Fechar</button>
        </div>
      )}
    </ModalShell>
  );
};

const EditUserModal = ({ open, target, onClose, onSaved }) => {
  const { token } = useAdminAuth();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && target) {
      setForm({
        nome: target.nome || '',
        email: target.email || '',
        telefone: target.telefone || '',
        role: target.role || 'user',
        status: target.status || 'ativo',
        observacoes: target.observacoes || '',
      });
      setSubmitting(false); setErr('');
    }
  }, [open, target]);

  if (!open || !target) return null;

  const submit = async (e) => {
    e?.preventDefault();
    setErr(''); setSubmitting(true);
    // Send only changed values (partial update).
    const patch = {};
    for (const [k, v] of Object.entries(form)) {
      if ((target[k] ?? '') !== v) patch[k] = v;
    }
    if (Object.keys(patch).length === 0) { onClose(); return; }
    try {
      await adminAxios(token).patch(`/api/admin/users/${target.id}`, patch);
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Falha ao salvar.');
    } finally { setSubmitting(false); }
  };

  return (
    <ModalShell open={open} onClose={onClose} title={`Editar — ${target.nome || target.email}`} testid="users-edit-modal">
      <form onSubmit={submit} className="space-y-3">
        {err && (
          <div className="flex items-start gap-2 p-2 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{err}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Nome</Label>
            <input value={form.nome || ''} onChange={(e)=>setForm({...form, nome:e.target.value})}
                   className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-edit-nome" />
          </div>
          <div>
            <Label>E-mail</Label>
            <input type="email" value={form.email || ''} onChange={(e)=>setForm({...form, email:e.target.value})}
                   className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-edit-email" />
          </div>
          <div>
            <Label>Telefone</Label>
            <input value={form.telefone || ''} onChange={(e)=>setForm({...form, telefone:e.target.value})}
                   className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-edit-telefone" />
          </div>
          <div>
            <Label>Status</Label>
            <select value={form.status || 'ativo'} onChange={(e)=>setForm({...form, status:e.target.value})}
                    className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-edit-status">
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="bloqueado">Bloqueado</option>
            </select>
          </div>
          <div className="col-span-2">
            <Label>Observações</Label>
            <textarea value={form.observacoes || ''} onChange={(e)=>setForm({...form, observacoes:e.target.value})}
                      rows={2} className="w-full mt-1 px-2 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="users-edit-obs" />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#475569] hover:bg-[#F8FAFC]"
                  data-testid="users-edit-cancel">Cancelar</button>
          <button type="submit" disabled={submitting}
                  className="flex-1 bg-[#1E3A5F] hover:bg-[#0F1C2E] text-white font-bold px-3 py-2 rounded text-sm disabled:opacity-60"
                  data-testid="users-edit-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Salvar alterações'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const ResetUserPwModal = ({ open, target, onClose }) => {
  const { token } = useAdminAuth();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [enviarEmail, setEnviarEmail] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) { setSubmitting(false); setResult(null); setErr(''); setEnviarEmail(false); setCopied(false); }
  }, [open, target]);

  if (!open || !target) return null;

  const confirm = async () => {
    setSubmitting(true); setErr('');
    try {
      const resp = await adminAxios(token).post(`/api/admin/users/${target.id}/reset-password`, { enviar_email: enviarEmail });
      setResult(resp.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Falha ao resetar senha.');
    } finally { setSubmitting(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result?.senha_temporaria || ''); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch { /* */ }
  };

  const whatsLink = result ? `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
    `Olá ${result.nome || ''}, sua senha no Tô de Olho foi redefinida.\nE-mail: ${result.email}\nSenha temporária: ${result.senha_temporaria}\n\nAcesse https://app.todeolho.inf.br/auth e altere em Perfil.`
  )}` : '#';

  return (
    <ModalShell open={open} onClose={onClose} title="Resetar senha do usuário" testid="users-reset-modal">
      {!result ? (
        <>
          <p className="text-sm text-[#0F1C2E] mb-2">
            Gerar nova senha temporária para <strong>{target.nome || target.email}</strong>?
          </p>
          <p className="text-xs text-[#475569] mb-3">
            {target.email}<br />
            A senha atual será invalidada. O usuário verá um aviso para alterá-la em Perfil.
          </p>
          <label className="flex items-center gap-2 text-xs text-[#475569] mb-3">
            <input type="checkbox" checked={enviarEmail} onChange={(e)=>setEnviarEmail(e.target.checked)}
                   data-testid="users-reset-enviar-email" />
            <Mail className="w-3.5 h-3.5" /> Enviar nova senha por e-mail (Resend)
          </label>
          {err && (
            <div className="flex items-start gap-2 p-2 mb-3 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{err}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={onClose}
                    className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#475569] hover:bg-[#F8FAFC]"
                    data-testid="users-reset-cancel">Cancelar</button>
            <button onClick={confirm} disabled={submitting}
                    className="flex-1 bg-[#1E3A5F] hover:bg-[#0F1C2E] text-white font-bold px-3 py-2 rounded text-sm disabled:opacity-60"
                    data-testid="users-reset-confirm">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Gerar senha temporária'}
            </button>
          </div>
        </>
      ) : (
        <div className="space-y-3">
          <div className="rounded border border-[#FCD34D] bg-[#FFFBEB] p-3">
            <p className="text-[11px] uppercase font-bold text-[#92400E] mb-1">Senha temporária — copie agora</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-base font-black text-[#0F1C2E] bg-white border border-[#FDE68A] rounded px-3 py-2 select-all"
                    data-testid="users-reset-temp-pw">{result.senha_temporaria}</code>
              <button onClick={copy}
                      className="px-2.5 py-2 bg-[#1E3A5F] hover:bg-[#16304b] text-white rounded text-xs font-bold inline-flex items-center gap-1"
                      data-testid="users-reset-copy">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
              </button>
            </div>
            {result.email_enviado && <p className="text-[10px] text-[#006030] mt-2">E-mail enviado ao usuário.</p>}
            {result.email_error && <p className="text-[10px] text-[#C62828] mt-1">E-mail falhou: {result.email_error}</p>}
          </div>
          <a href={whatsLink} target="_blank" rel="noopener noreferrer"
             className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-3 py-2 rounded text-sm"
             data-testid="users-reset-whatsapp">
            <MessageCircle className="w-4 h-4" /> Enviar credenciais pelo WhatsApp
          </a>
          <button onClick={onClose}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#475569] hover:bg-[#F8FAFC]"
                  data-testid="users-reset-close">Fechar</button>
        </div>
      )}
    </ModalShell>
  );
};

const AdminUsers = () => {
  const { token, isSuper } = useAdminAuth();
  const ax = useMemo(() => adminAxios(token), [token]);

  const [metrics, setMetrics] = useState(null);
  const [series, setSeries] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ plano: 'all', uf: 'all', period: 'all', status: 'all', search: '' });
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setErr('');
    try {
      // Status is filtered client-side because the backend `/users` already
      // returns the field — avoids changing the contract.
      const { status: _statusFilter, ...rest } = filters;
      const params = new URLSearchParams(rest).toString();
      const [m, s, f, u] = await Promise.all([
        ax.get('/api/admin/users/metrics'),
        ax.get('/api/admin/users/cadastros-diarios?days=30'),
        ax.get('/api/admin/users/funnel'),
        ax.get(`/api/admin/users?${params}`),
      ]);
      setMetrics(m.data);
      setSeries(s.data?.series || []);
      setFunnel(f.data);
      let list = u.data?.users || [];
      if (filters.status !== 'all') list = list.filter((x) => (x.status || 'ativo') === filters.status);
      setUsers(list);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Falha ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filters.plano, filters.uf, filters.period, filters.status]);

  const onSearchTrigger = () => loadAll();

  const blockUser = async (u) => {
    if (!isSuper) return;
    const motivo = window.prompt(
      u.status === 'bloqueado'
        ? 'Confirme o motivo do desbloqueio (mínimo 3 caracteres):'
        : `Motivo do bloqueio de ${u.email} (mínimo 3 caracteres):`,
      u.status === 'bloqueado' ? 'Reativando após análise' : '',
    );
    if (!motivo || motivo.trim().length < 3) return;
    setActingId(u.id);
    try {
      await ax.post(`/api/admin/users/${u.id}/block`, { motivo: motivo.trim() });
      await loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Falha ao atualizar usuário');
    } finally {
      setActingId(null);
    }
  };

  const changePlan = async (u) => {
    if (!isSuper) return;
    const nextPlan = u.plano === 'pro' ? 'free' : 'pro';
    let duracao_dias = null;
    if (nextPlan === 'pro') {
      const ans = window.prompt(`Duração em dias para conceder Pro a ${u.email}:`, '30');
      if (!ans) return;
      duracao_dias = parseInt(ans, 10);
      if (Number.isNaN(duracao_dias) || duracao_dias <= 0) return alert('Duração inválida');
    } else {
      const ok = window.confirm(`Rebaixar ${u.email} para Free?`);
      if (!ok) return;
    }
    setActingId(u.id);
    try {
      await ax.post(`/api/admin/users/${u.id}/plan`, { plano: nextPlan, duracao_dias });
      await loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Falha ao trocar plano');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-[#0F1C2E]">Usuários</h1>
            <p className="text-xs text-[#475569]">Visão operacional da base · {metrics?.total ?? '—'} cadastrados</p>
          </div>
          {isSuper && (
            <button onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-xs font-bold bg-[#006030] hover:bg-[#004020] text-white"
                    data-testid="users-create-btn">
              <UserPlus className="w-3.5 h-3.5" /> Novo usuário
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Users} color="#1E3A5F" label="Total cadastrados"
             value={metrics?.total ?? 0} testid="kpi-users-total" />
        <Kpi icon={UserCheck} color="#006030" label="Ativos últimos 7 dias"
             value={metrics?.ativos_7d ?? 0} testid="kpi-users-active" />
        <Kpi icon={Crown} color="#F59E0B" label="Pagantes (Pro)"
             value={metrics?.pagantes ?? 0} testid="kpi-users-paying" />
        <Kpi icon={AlertTriangle} color="#DC2626" label="Free no limite"
             value={metrics?.no_limite ?? 0}
             note={`>= ${metrics?.free_consultation_limit ?? 3} consultas`}
             testid="kpi-users-limit" />
      </div>

      {/* Sparkline + funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="signups-daily">
          <h3 className="text-sm font-bold text-[#0F1C2E] mb-3">Cadastros diários — últimos 30 dias</h3>
          <Sparkline series={series} />
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
          <h3 className="text-sm font-bold text-[#0F1C2E] mb-3">Funil de conversão</h3>
          <Funnel data={funnel} />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 grid grid-cols-2 lg:grid-cols-6 gap-2">
        <select value={filters.plano} onChange={(e) => setFilters({ ...filters, plano: e.target.value })}
                className="px-2 py-2 border border-[#E2E8F0] rounded text-xs" data-testid="filter-users-plano">
          <option value="all">Todos planos</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-2 py-2 border border-[#E2E8F0] rounded text-xs" data-testid="filter-users-status">
          <option value="all">Todos status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
          <option value="bloqueado">Bloqueado</option>
        </select>
        <select value={filters.uf} onChange={(e) => setFilters({ ...filters, uf: e.target.value })}
                className="px-2 py-2 border border-[#E2E8F0] rounded text-xs" data-testid="filter-users-uf">
          <option value="all">Todos estados</option>
          {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
        </select>
        <select value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                className="px-2 py-2 border border-[#E2E8F0] rounded text-xs" data-testid="filter-users-period">
          <option value="all">Todo o período</option>
          <option value="7d">Últimos 7 dias</option>
          <option value="30d">Últimos 30 dias</option>
          <option value="90d">Últimos 90 dias</option>
        </select>
        <div className="relative col-span-2 lg:col-span-1">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input value={filters.search}
                 onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                 onKeyDown={(e) => e.key === 'Enter' && onSearchTrigger()}
                 onBlur={onSearchTrigger}
                 placeholder="Nome ou e-mail"
                 className="w-full pl-7 pr-2 py-2 border border-[#E2E8F0] rounded text-xs"
                 data-testid="filter-users-search" />
        </div>
        <button onClick={loadAll}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-bold bg-[#1E3A5F] hover:bg-[#0F1C2E] text-white"
                data-testid="users-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" /></div>
      ) : err ? (
        <p className="text-sm text-[#DC2626]">{err}</p>
      ) : users.length === 0 ? (
        <p className="text-sm text-[#94A3B8] text-center py-12">Nenhum usuário encontrado.</p>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#475569] border-b border-[#E2E8F0]">
                <th className="px-3 py-2 text-left">Nome</th>
                <th className="px-3 py-2 text-left">E-mail</th>
                <th className="px-3 py-2 text-left">UF</th>
                <th className="px-3 py-2 text-left">Plano</th>
                <th className="px-3 py-2 text-right">Consultas</th>
                <th className="px-3 py-2 text-left">Cadastro</th>
                <th className="px-3 py-2 text-left">Último acesso</th>
                <th className="px-3 py-2 text-left">Status</th>
                {isSuper && <th className="px-3 py-2 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#F4F6F8] hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2 text-[#0F1C2E] text-xs font-medium truncate max-w-[180px]">{u.nome || '—'}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs truncate max-w-[200px]">{u.email}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs">{u.uf || '—'}</td>
                  <td className="px-3 py-2"><PlanBadge plano={u.plano} /></td>
                  <td className="px-3 py-2 text-[#0F1C2E] text-xs font-bold text-right">{u.consultas_count}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs whitespace-nowrap">{fmtDate(u.criado_em)}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs whitespace-nowrap">{fmtDate(u.ultimo_acesso)}</td>
                  <td className="px-3 py-2"><StatusBadge status={u.status} /></td>
                  {isSuper && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setEditTarget(u)}
                          title="Editar usuário"
                          className="p-1.5 rounded bg-[#F4F6F8] hover:bg-[#E2E8F0] text-[#475569]"
                          data-testid={`user-edit-${u.id}`}
                        ><Pencil className="w-3 h-3" /></button>
                        <button
                          onClick={() => setResetTarget(u)}
                          title="Resetar senha (gera senha temporária)"
                          className="p-1.5 rounded bg-[#FEF3C7] hover:bg-[#FDE68A] text-[#92400E]"
                          data-testid={`user-reset-pw-${u.id}`}
                        ><KeyRound className="w-3 h-3" /></button>
                        <button
                          disabled={actingId === u.id}
                          onClick={() => blockUser(u)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold ${
                            u.status === 'bloqueado'
                              ? 'bg-[#DCFCE7] text-[#006030] hover:bg-[#BBF7D0]'
                              : 'bg-[#FEE2E2] text-[#7F1D1D] hover:bg-[#FECACA]'
                          } disabled:opacity-50`}
                          data-testid={`user-block-${u.id}`}
                        >
                          {u.status === 'bloqueado' ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                          {u.status === 'bloqueado' ? 'Desbloquear' : 'Bloquear'}
                        </button>
                        <button
                          disabled={actingId === u.id}
                          onClick={() => changePlan(u)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A] disabled:opacity-50"
                          data-testid={`user-plan-${u.id}`}
                        >
                          <Crown className="w-3 h-3" />
                          {u.plano === 'pro' ? 'Para Free' : 'Para Pro'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={loadAll} />
      <EditUserModal open={!!editTarget} target={editTarget} onClose={() => setEditTarget(null)} onSaved={loadAll} />
      <ResetUserPwModal open={!!resetTarget} target={resetTarget} onClose={() => setResetTarget(null)} />
    </div>
  );
};

export default AdminUsers;
